import { useEffect, useRef } from "react";
import { createMultiFingerTap, isBugReportShortcut } from "../lib/bugReportTriggers.js";

/**
 * Opens the bug-report modal from anywhere in the app, without its button: the keyboard
 * shortcut (`isBugReportShortcut`, works while typing) and a three-finger tap on a touch
 * screen. Each opens through `openBugReport(via)`, so the open is
 * recorded with how it was reached; none acts while the modal is already open.
 *
 * @param {{ openBugReport: (via: string) => void, bugOpen: boolean }} deps
 */
export function useBugReportTriggers({ openBugReport, bugOpen }) {
  const stateRef = useRef({ openBugReport, bugOpen });
  useEffect(() => {
    stateRef.current = { openBugReport, bugOpen };
  }, [openBugReport, bugOpen]);

  useEffect(() => {
    const open = (via) => {
      if (!stateRef.current.bugOpen) stateRef.current.openBugReport(via);
    };
    // Capture phase on window, ahead of the document-level hotkey and type-to-chat handlers.
    const onKeyDown = (e) => {
      if (!isBugReportShortcut(e)) return;
      e.preventDefault();
      e.stopPropagation();
      open("shortcut");
    };
    const tap = createMultiFingerTap({ fingers: 3 });
    const onTouchStart = (e) => {
      for (const t of e.changedTouches) tap.down(t.identifier, t.clientX, t.clientY, e.timeStamp);
    };
    const onTouchMove = (e) => {
      for (const t of e.changedTouches) tap.move(t.identifier, t.clientX, t.clientY);
    };
    const onTouchEnd = (e) => {
      let fired = false;
      for (const t of e.changedTouches) fired = tap.up(t.identifier, e.timeStamp) || fired;
      if (fired) open("gesture");
    };
    const onTouchCancel = (e) => {
      for (const t of e.changedTouches) tap.cancel(t.identifier);
    };
    // telemetry-gesture: opens the bug-report modal; ui.bug_report.open records via (shortcut / gesture)
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    window.addEventListener("touchmove", onTouchMove, { capture: true, passive: true });
    window.addEventListener("touchend", onTouchEnd, { capture: true, passive: true });
    window.addEventListener("touchcancel", onTouchCancel, { capture: true, passive: true });
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("touchstart", onTouchStart, { capture: true });
      window.removeEventListener("touchmove", onTouchMove, { capture: true });
      window.removeEventListener("touchend", onTouchEnd, { capture: true });
      window.removeEventListener("touchcancel", onTouchCancel, { capture: true });
    };
  }, []);
}
